import React, { useEffect, useState, useContext, Fragment } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import { Formik, Form, Field } from 'formik'
import CustomLoader from '../layouts/CustomLoader'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { DateTime } from 'luxon'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'
import { useTranslation } from 'react-i18next'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface PayerEmail  { email: string }
interface Status      { name: string }
interface Product     { name: string }
interface PaymentPlan { purchased_amount: number }

interface Plan {
  name        : string
  Product     : Product
  PaymentPlan : PaymentPlan
}

interface Package {
  serial_number : string
  createdAt     : string
  TimeId        : number | string
  expiry_time   : number
  Status        : Status
}

interface ItemPayment {
  total_amount      : string
  individual_amount : string
}

interface Payment {
  createdAt       : string
  payment_status  : string
  pi_stripe_id    : string
  Payeremail      : PayerEmail
  Itempayments    : ItemPayment[]
  Plans           : Plan[]
  Packages        : Package[]
}

const Customer: React.FC = () => {
  const { getHeaders }             = useContext(AuthContext) as AuthCtx
  const { t }                      = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const [stripe, setStripe]        = useState('')
  const [data, setData]            = useState<Payment[]>([])
  const [loading, setLoading]      = useState(false)
  const [totalRows, setTotalRows]  = useState(0)
  const [perPage, setPerPage]      = useState(10)
  const [searchOpen, setSearchOpen] = useState(false)

  const fetchPayments = async (page: number, perPageVal = perPage, stripeVal = stripe) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res = await axios.get<{ message: Payment[]; total: number }>(
        `${url}payments/customer/?page=${page}&per_page=${perPageVal}&stripe=${stripeVal}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (page: number) => fetchPayments(page)

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    await fetchPayments(page, newPerPage)
  }

  const handleSearch = async (values: { stripe: string }) => {
    setStripe(values.stripe)
    await fetchPayments(1, perPage, values.stripe)
  }

  const downloadInvoice = async (piStripeId: string) => {
    try {
      const res  = await axios.post<{ message: string; name: string }>(
        `${url}payments/customer-pdf`,
        { pi: piStripeId }
      )
      const link    = document.createElement('a')
      link.href     = `data:application/pdf;base64,${res.data.message}`
      link.download = res.data.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const sumTotal = (items: ItemPayment[]) =>
    items.reduce((sum, e) => sum + parseFloat(e.total_amount), 0).toFixed(2)

  useEffect(() => {
    fetchPayments(1)
  }, [])

  const columns = [
    {
      maxWidth : '35%',
      name     : t('payments.customer.stripe_data'),
      cell     : (row: Payment) => {
        const date = DateTime.fromISO(row.createdAt, { zone: 'utc' })
        return (
          <div className="py-2 w-100">
            <div className="mb-2">
              <span className="fw-bold text-muted small">{t('payments.customer.payer_email')}: </span>
              <span className="small">{row.Payeremail.email}</span>
            </div>
            <div className="mb-2">
              <span className="fw-bold text-muted small">{t('payments.customer.date_purchase')}: </span>
              <span className="small">{date.toFormat('yyyy-MM-dd HH:mm:ss')}</span>
            </div>
            <div className="mb-2">
              <span className="fw-bold text-muted small">{t('payments.customer.amount_total')}: </span>
              <span className="small fw-bold">${sumTotal(row.Itempayments)}</span>
            </div>
            <div>
              <span className="fw-bold text-muted small">{t('payments.customer.status_payment')}: </span>
              <span className={`badge rounded-pill text-uppercase fw-bold px-2 py-1 ${
                row.payment_status === 'paid' ? 'text-bg-success' : 'text-bg-warning'
              }`}>
                {row.payment_status}
              </span>
            </div>
          </div>
        )
      },
    },
    {
      maxWidth : '25%',
      name     : t('payments.customer.product'),
      cell     : (row: Payment) => {
        let counter = 0
        return (
          <ul className="ps-0 my-2 w-100">
            {row.Plans.map((plan, i) => {
              const slots = Array.from({ length: plan.PaymentPlan.purchased_amount })
              return slots.map((_slot, j) => {
                counter++
                return (
                  <Fragment key={`plan-${i}-${j}`}>
                    <li className="list-group-item pb-1 mb-1 border-bottom">
                      <div className="row g-0">
                        <div className="col-2"><b>{counter}.-</b></div>
                        <div className="col-10">
                          <div className="small"><b>Plan: </b>{plan.name}</div>
                          <div className="small"><b>{t('payments.customer.product')}: </b>{plan.Product.name}</div>
                          <div className="small">
                            <b>{t('payments.customer.price')}: </b>
                            ${parseFloat(row.Itempayments[i]?.individual_amount ?? '0').toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </li>
                  </Fragment>
                )
              })
            })}
          </ul>
        )
      },
    },
    {
      maxWidth : '25%',
      name     : t('payments.customer.package'),
      cell     : (row: Payment) => (
        <ul className="ps-0 my-2 w-100">
          {row.Packages.map((pkg, i) => {
            const timeId = parseInt(String(pkg.TimeId))
            const expiry = DateTime.fromISO(pkg.createdAt).plus({
              years  : timeId === 1 ? pkg.expiry_time : 0,
              months : timeId === 2 ? pkg.expiry_time : 0,
              days   : timeId === 3 ? pkg.expiry_time : 0,
            }).toISODate()
            return (
              <Fragment key={`pkg-${i}`}>
                <li className="list-group-item pb-1 mb-1 border-bottom">
                  <div className="row g-0">
                    <div className="col-2"><b>{i + 1}.-</b></div>
                    <div className="col-10">
                      <div className="small"><b>{t('payments.customer.package')}: </b>{pkg.serial_number}</div>
                      <div className="small"><b>{t('payments.customer.status')}: </b>{pkg.Status.name}</div>
                      <div className="small"><b>{t('payments.customer.expire_at')}: </b>{expiry}</div>
                    </div>
                  </div>
                </li>
              </Fragment>
            )
          })}
        </ul>
      ),
    },
    {
      maxWidth : '15%',
      name     : t('payments.customer.actions'),
      cell     : (row: Payment) =>
        row.payment_status !== 'refunded' ? (
          <button
            type="button"
            className="btn btn-sm btn-warning fw-bold"
            onClick={() => downloadInvoice(row.pi_stripe_id)}
          >
            <FontAwesomeIcon icon={faFilePdf} className="me-1" />
            Invoice
          </button>
        ) : null,
    },
  ]

  return (
    <div className="row align-items-start">
      <div className="col-sm-4 col-md-2 mb-3 my-1">
        <span className="text-capitalize fw-bold">{t('payments.customer.payments')}</span>
        <p className="text-muted small mt-1">{t('payments.customer.billing')}</p>
      </div>

      <div className="col-sm-8 col-md-10 mb-3">
        <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <button
            type="button"
            onClick={() => setSearchOpen(prev => !prev)}
            style={{
              width           : '100%',
              display         : 'flex',
              alignItems      : 'center',
              gap             : '0.5rem',
              padding         : '0.75rem 1.25rem',
              background      : searchOpen ? '#f8fafc' : '#fff',
              border          : 'none',
              borderBottom    : searchOpen ? '1px solid #e2e8f0' : 'none',
              cursor          : 'pointer',
              fontWeight      : 700,
              fontSize        : '0.9rem',
              color           : '#475569',
              textAlign       : 'left',
              transition      : 'background 0.15s',
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <span>{t('utilities.search')}</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>

          {searchOpen && (
            <div style={{ padding: '1.25rem' }}>
              <Formik initialValues={{ stripe }} onSubmit={handleSearch} enableReinitialize>
                <Form>
                  <div className="row align-items-center mb-3">
                    <label htmlFor="stripe" className="col-sm-2 col-form-label col-form-label-sm">
                      {t('payments.customer.search.stripe_payment')}
                    </label>
                    <div className="col-sm-10">
                      <Field
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t('payments.customer.search.stripe_payment')}
                        name="stripe"
                        id="stripe"
                      />
                    </div>
                  </div>
                  <button className="btn btn-sm btn-bd-primary" type="submit">
                    <span className="fw-bold">{t('utilities.search_btn')}</span>
                  </button>
                </Form>
              </Formik>
            </div>
          )}
        </div>
      </div>

      <div className="col-md-12">
        <div className="card card-shadow">
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={loading}
            pagination
            paginationPerPage={perPage}
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={paginationComponentOptions}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressComponent={<CustomLoader />}
            dense
          />
        </div>
      </div>
    </div>
  )
}

export default Customer
